import { useCallback } from 'react';

import Typography from '@dynatrace/strato-design-tokens/typography';

import { ELLIPSIS_CHARACTER } from '../../../../typography/utils.js';
import { useCanvas } from '../../../core/components/canvas/hooks/useCanvas.js';
import { charsFontWidth12pixelsSize400Map } from '../../../core/components/text-ellipsis/chars-font-width-map.js';
import {
  calculateWidth,
  truncateString,
} from '../../../core/components/text-ellipsis/truncate-string.js';
import { getFontDetails } from '../../../core/utils/get-font-details.js';
import {
  DEFAULT_CLUSTER_LABEL_FONTSIZE,
  DEFAULT_CLUSTER_LABEL_LINE_HEIGHT,
  DEFAULT_FONT_WEIGHT,
  TEXT_ALIGN,
  TEXT_BASELINE,
} from '../../constants.js';
import { useTruncationMode } from '../../hooks/useTruncationMode.js';

export interface TreeMapNodeLabelProps {
  x: number;
  y: number;
  color: string;
  label: string;
  maxWidth: number;
  opacity?: number;
}

export const ItemFontStyles = {
  fontWeight: Typography.Text.Small.Default.Weight,
  fontSize: Typography.Text.Small.Emphasized.Size,
  fontFamily: Typography.Text.Small.Default.Family,
};

export const TreeMapNodeLabel = (props: TreeMapNodeLabelProps) => {
  const { x, y, color, label, maxWidth, opacity = 1 } = props;

  const truncationMode = useTruncationMode();

  const draw = useCallback(
    (ctx: CanvasRenderingContext2D) => {
      ctx.font = getFontDetails(
        DEFAULT_FONT_WEIGHT,
        DEFAULT_CLUSTER_LABEL_FONTSIZE,
        DEFAULT_CLUSTER_LABEL_LINE_HEIGHT,
      );
      ctx.fillStyle = color;
      const estimatedLabelWidth = Math.ceil(
        calculateWidth(label, charsFontWidth12pixelsSize400Map),
      );

      const truncated =
        estimatedLabelWidth > maxWidth
          ? truncateString(
              label,
              maxWidth,
              charsFontWidth12pixelsSize400Map,
              truncationMode,
              'start',
            )
          : label;

      const sanitizedLabel = truncated === ELLIPSIS_CHARACTER ? '' : truncated;

      ctx.textAlign = TEXT_ALIGN.Center;
      ctx.textBaseline = TEXT_BASELINE.Alphabetic;
      ctx.globalAlpha = opacity;
      ctx.fillText(sanitizedLabel, x, y, maxWidth);

      return false;
    },
    [color, label, maxWidth, truncationMode, opacity, x, y],
  );

  useCanvas('treemap-label', draw);

  return null;
};
